import React from 'react';
import { Reveal } from '../motion/Reveal';

/**
 * Petits blocs typographiques réutilisés dans toutes les sections marketing :
 * eyebrow (sur-titre), titre de section, et conteneur de section normalisé.
 */

export function Eyebrow({
  children,
  className,
  tone = 'dark',
}: {
  children: React.ReactNode;
  className?: string;
  tone?: 'dark' | 'light';
}) {
  const color = tone === 'light' ? 'text-gold-300' : 'text-gold-600';
  return (
    <span data-component-id="src/components/marketing/ui/Section.tsx:20:4" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="20" data-component-end-line="25" data-component-file="Section.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
      className={`inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] ${color} ${className ?? ''}`}
    >
      <span data-component-id="src/components/marketing/ui/Section.tsx:23:6" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="23" data-component-end-line="23" data-component-file="Section.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22h-px%20w-6%20bg-current%20opacity-60%22%7D" className="h-px w-6 bg-current opacity-60" aria-hidden="true" />
      {children}
    </span>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  intro,
  align = 'left',
  tone = 'dark',
  className,
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  intro?: React.ReactNode;
  align?: 'left' | 'center';
  tone?: 'dark' | 'light';
  className?: string;
}) {
  const isCenter = align === 'center';
  const titleColor = tone === 'light' ? 'text-white' : 'text-ink';
  const introColor = tone === 'light' ? 'text-white/55' : 'text-ink-500';

  return (
    <div data-component-id="src/components/marketing/ui/Section.tsx:49:4" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="49" data-component-end-line="71" data-component-file="Section.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
      className={`${isCenter ? 'mx-auto text-center' : ''} max-w-2xl ${className ?? ''}`}
    >
      {eyebrow && (
        <Reveal data-component-id="src/components/marketing/ui/Section.tsx:53:8" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="53" data-component-end-line="57" data-component-file="Section.tsx" data-component-name="Reveal" data-component-content="%7B%22elementName%22%3A%22Reveal%22%7D">
          <div data-component-id="src/components/marketing/ui/Section.tsx:54:10" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="54" data-component-end-line="56" data-component-file="Section.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={isCenter ? 'flex justify-center' : ''}>
            <Eyebrow data-component-id="src/components/marketing/ui/Section.tsx:55:12" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="55" data-component-end-line="55" data-component-file="Section.tsx" data-component-name="Eyebrow" data-component-content="%7B%22elementName%22%3A%22Eyebrow%22%7D" tone={tone}>{eyebrow}</Eyebrow>
          </div>
        </Reveal>
      )}
      <Reveal data-component-id="src/components/marketing/ui/Section.tsx:59:6" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="59" data-component-end-line="65" data-component-file="Section.tsx" data-component-name="Reveal" data-component-content="%7B%22elementName%22%3A%22Reveal%22%7D" delay={0.06}>
        <h2 data-component-id="src/components/marketing/ui/Section.tsx:60:8" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="60" data-component-end-line="64" data-component-file="Section.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%7D"
          className={`font-display font-light tracking-tight ${titleColor} mt-5 text-[clamp(2rem,4.4vw,3.4rem)] leading-[1.08]`}
        >
          {title}
        </h2>
      </Reveal>
      {intro && (
        <Reveal data-component-id="src/components/marketing/ui/Section.tsx:67:8" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="67" data-component-end-line="69" data-component-file="Section.tsx" data-component-name="Reveal" data-component-content="%7B%22elementName%22%3A%22Reveal%22%7D" delay={0.12}>
          <p data-component-id="src/components/marketing/ui/Section.tsx:68:10" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="68" data-component-end-line="68" data-component-file="Section.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%7D" className={`mt-5 text-[16px] leading-relaxed ${introColor}`}>{intro}</p>
        </Reveal>
      )}
    </div>
  );
}

/** Conteneur de section avec espacement vertical et largeur maximale cohérents. */
export function Section({
  children,
  className,
  id,
  container = true,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
  container?: boolean;
}) {
  return (
    <section data-component-id="src/components/marketing/ui/Section.tsx:88:4" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="88" data-component-end-line="94" data-component-file="Section.tsx" data-component-name="section" data-component-content="%7B%22elementName%22%3A%22section%22%2C%22id%22%3A%22%5Bvar%3Aid%5D%22%7D" id={id} className={`relative py-24 sm:py-28 lg:py-32 ${className ?? ''}`}>
      {container ? (
        <div data-component-id="src/components/marketing/ui/Section.tsx:90:8" data-component-path="src/components/marketing/ui/Section.tsx" data-component-line="90" data-component-end-line="90" data-component-file="Section.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mx-auto%20w-full%20max-w-screen-2xl%20px-6%20sm%3Apx-8%20lg%3Apx-12%22%7D" className="mx-auto w-full max-w-screen-2xl px-6 sm:px-8 lg:px-12">{children}</div>
      ) : (
        children
      )}
    </section>
  );
}
